import type { ButtonHTMLAttributes, InputHTMLAttributes, ReactNode } from 'react';

function cx(...parts: Array<string | false | null | undefined>): string {
  return parts.filter(Boolean).join(' ');
}

// ---------------------------------------------------------------------------

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  loading?: boolean;
}

const BUTTON_VARIANTS: Record<ButtonVariant, string> = {
  primary: 'bg-accent text-accent-fg hover:bg-accent-hover',
  secondary: 'bg-surface-raised text-text border border-border-strong hover:border-accent',
  ghost: 'bg-transparent text-text-muted hover:text-text hover:bg-surface-raised',
  danger: 'bg-transparent text-danger border border-danger/40 hover:bg-danger/10',
};

export function Button({
  variant = 'primary',
  loading = false,
  disabled,
  children,
  className,
  ...rest
}: ButtonProps) {
  return (
    <button
      {...rest}
      disabled={disabled || loading}
      className={cx(
        'inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2.5',
        'text-sm font-medium transition-colors',
        'disabled:cursor-not-allowed disabled:opacity-50',
        BUTTON_VARIANTS[variant],
        className,
      )}
    >
      {loading && (
        <span
          aria-hidden
          className="size-3.5 animate-spin rounded-full border-2 border-current border-t-transparent"
        />
      )}
      {children}
    </button>
  );
}

// ---------------------------------------------------------------------------

interface FieldProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string;
  hint?: string;
  error?: string | null;
}

export function Field({ label, hint, error, className, id, ...rest }: FieldProps) {
  const inputId = id ?? `field-${label.toLowerCase().replace(/\s+/g, '-')}`;
  const describedBy = error ? `${inputId}-error` : hint ? `${inputId}-hint` : undefined;

  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={inputId} className="text-sm font-medium text-text">
        {label}
      </label>
      <input
        {...rest}
        id={inputId}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy}
        className={cx(
          'w-full rounded-lg border bg-surface px-3 py-2.5 text-text',
          'placeholder:text-text-faint',
          'focus:outline-none focus-visible:outline-2 focus-visible:outline-accent',
          error ? 'border-danger' : 'border-border-strong',
          className,
        )}
      />
      {error ? (
        <p id={`${inputId}-error`} role="alert" className="text-sm text-danger">
          {error}
        </p>
      ) : hint ? (
        <p id={`${inputId}-hint`} className="text-sm text-text-faint">
          {hint}
        </p>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------

export function Card({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cx(
        'rounded-xl border border-border bg-surface p-5 shadow-[var(--shadow)]',
        className,
      )}
    >
      {children}
    </div>
  );
}

export function Banner({
  tone = 'info',
  children,
}: {
  tone?: 'info' | 'success' | 'danger';
  children: ReactNode;
}) {
  const tones = {
    info: 'border-border-strong bg-surface-raised text-text-muted',
    success: 'border-success/40 bg-success/10 text-text',
    danger: 'border-danger/40 bg-danger/10 text-text',
  } as const;

  return (
    <div role={tone === 'danger' ? 'alert' : undefined} className={cx('rounded-lg border px-4 py-3 text-sm', tones[tone])}>
      {children}
    </div>
  );
}

export function Spinner({ label }: { label: string }) {
  return (
    <div className="flex items-center justify-center gap-3 py-16 text-text-muted">
      <span
        aria-hidden
        className="size-4 animate-spin rounded-full border-2 border-current border-t-transparent"
      />
      <span className="text-sm">{label}</span>
    </div>
  );
}

/**
 * Whether an avatar value is an uploaded picture rather than an emoji.
 *
 * Matched against the exact shape the server produces — a content-addressed
 * name under `/uploads/` — rather than a loose "starts with a slash". The
 * server refuses to store anything else, so this is really a second opinion,
 * but it is the one that decides whether a string ends up inside an `img src`,
 * and that is worth being strict about.
 */
export function isUploadedAvatar(value: string): boolean {
  return /^\/uploads\/[0-9a-f]{32}\.webp$/.test(value);
}

export function Avatar({ value, size = 'md' }: { value: string; size?: 'sm' | 'md' | 'lg' }) {
  const sizes = { sm: 'size-7 text-base', md: 'size-10 text-xl', lg: 'size-16 text-3xl' };
  const shell = cx(
    'inline-flex shrink-0 items-center justify-center overflow-hidden rounded-full bg-surface-raised',
    sizes[size],
  );

  if (isUploadedAvatar(value)) {
    return (
      <span aria-hidden className={shell}>
        <img src={value} alt="" className="size-full object-cover" loading="lazy" />
      </span>
    );
  }

  return (
    <span aria-hidden className={shell}>
      {value}
    </span>
  );
}
